/**
 * Session boot — spec §D1 (`GET /auth/me`) wired into the §F1 store.
 *
 * NOTES #133: until now the SPA's `role` was a client-side fiction. `useSessionQuery`
 * makes `/auth/me` the source of truth — it runs once at boot and writes `role` + `user`,
 * and the §I2 gating in `lib/access.ts` follows. It is still only a *mirror*: the server
 * decides (§I2 "never the enforcement").
 *
 * The query deliberately does **not** retry and does **not** refetch: an unreachable
 * `/auth/me` means "browse as public", which is a valid, useful state (§D1), and a
 * refetch would silently undo the §F3 `?demo=1` switcher mid-session.
 *
 * The component that mounts this hook is `app/SessionSync.tsx` — split out so this
 * module stays free of components (react-refresh).
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchMe, queryKeys } from './api';
import { useAppStore } from './state';

/** The mockup's avatar chip when nobody is signed in ("A. Ouédraogo" persona). */
export const ANONYMOUS_INITIALS = 'AO';

/**
 * Avatar initials for a §D1 `name`: first letter of the first and last word, upper-cased.
 * Anonymous sessions keep the mockup's "AO" rather than a "?" — §F3 describes an avatar
 * menu and the mockup renders that chip identically whether or not anyone is signed in.
 */
export function initials(name: string | null | undefined): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return ANONYMOUS_INITIALS;
  const first = words[0] ?? '';
  const last = words[words.length - 1] ?? '';
  return ((first[0] ?? '') + (words.length > 1 ? (last[0] ?? '') : '')).toUpperCase();
}

/** Boot-time `useQuery(['me'])` (§F1 key) that pushes §D1's answer into the store. */
export function useSessionQuery(): void {
  const { data } = useQuery({
    queryKey: queryKeys.me(),
    queryFn: fetchMe,
    retry: false,
    staleTime: Infinity,
  });
  const setSession = useAppStore((s) => s.setSession);

  useEffect(() => {
    if (data) setSession(data.user);
  }, [data, setSession]);
}
